import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Link, Grid, Button, Table, TableBody, TableCell, TableContainer,
  TableFooter, TablePagination, TableRow, Paper, TableHead,
  InputLabel, MenuItem, FormControl, Select as Choice
} from "@mui/material";
import PageTitle from "../../common/PageTitle";
import { axiosInstance } from "../../../utils/axiosSetup";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { getAPI } from "../../network";
import Loader from "../../common/Loader";
import EmptyTable from "../../common/EmptyTable";
import { checkAuthority, tableHeader, tableData } from "../../utils";
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import LocalDateSelector from "../../common/LocalDateSelector";

export default function TimesheetPage() {
  const navigateTo = useNavigate();
  const companyId = localStorage.getItem("companyIdLS") || undefined;

  const [selectedSite, setSelectedSite] = useState('');
  const [selectedGuard, setSelectedGuard] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedDate, setSelectedDate] = useState(null);
  const [vendorF, setVendorF] = useState(null);

  const [sites, setSites] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [guardList, setGuardList] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [permissionList, setPermissionList] = useState([]);

  const [allTimesheets, setAllTimesheets] = useState([]); // Raw API response
  const [filteredUsers, setFilteredUsers] = useState([]); // UI table data
  const [startDateFilter, setStartDateFilter] = useState(null);
  const [endDateFilter, setEndDateFilter] = useState(null);

  const [loader, setLoader] = useState(false);
  const [perPages] = useState([10, 25, 50]);
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(0);

  const submenuPermissions = permissionList[0]?.submenuPermissions || [];
  const canView = submenuPermissions.some((p) => p.submenu === "View");
  const showBothButtons = submenuPermissions.length === 0;

  useEffect(() => {
    const permissions = JSON.parse(localStorage.getItem("permisionlist")) || [];
    setPermissionList(permissions);
  }, []);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoader(true);
    await Promise.all([
      fetchSites(),
      fetchGuards(),
      fetchStatuses(),
      fetchVendors(),
      fetchTimeSheets()
    ]);
    setLoader(false);
  };

  const fetchSites = async () => {
    const data = await getAPI(`/sites?companyId=${companyId}`);
    if (data) setSites(data);
  };

  const fetchGuards = async () => {
    const data = await getAPI(`/admin/guards?companyId=${companyId}`);
    if (data) setGuardList(data);
  };

  const fetchStatuses = async () => {
    const data = await getAPI(`/timesheet-statuses?companyId=${companyId}`);
    if (data) setStatusList(data);
  };

  const fetchVendors = async () => {
    const data = await getAPI(`/vendors?companyId=${companyId}`);
    if (data) {
      const formatted = data.map((item) => ({
        id: item.id,
        label: item.name,
        value: item.id
      }));
      setVendors(formatted);
    }
  };

  const fetchTimeSheets = async () => {
    const data = await getAPI(`/admin/timesheet?companyId=${companyId}`);
    if (data) {
      setAllTimesheets(data);
      setFilteredUsers(data);
    }
  };
  // Local Filtering when filters change
  useEffect(() => {
    let filtered = [...allTimesheets];

    if (selectedSite) {
      filtered = filtered.filter(item => item.siteId?._id === selectedSite);
    }
    if (selectedGuard) {
      filtered = filtered.filter(item => item.userId?._id === selectedGuard);
    }
    if (vendorF) {
      filtered = filtered.filter(item => item.vendorId?._id === vendorF);
    }
    if (selectedStatus) {
      filtered = filtered.filter(item => item.statusId?._id === selectedStatus);
    }

    if (startDateFilter) {
      const start = new Date(startDateFilter).setHours(0, 0, 0, 0);
      filtered = filtered.filter(item => new Date(item.createdAt) >= start);
    }

    if (endDateFilter) {
      const end = new Date(endDateFilter).setHours(23, 59, 59, 999);
      filtered = filtered.filter(item => new Date(item.createdAt) <= end);
    }

    setFilteredUsers(filtered);
    setPage(0);
  }, [selectedSite, selectedGuard, vendorF, selectedStatus, startDateFilter, endDateFilter, allTimesheets]);


  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = (event) => {
    setPerPage(Number(event.target.value));
    setPage(0);
  };

  const clearFilter = () => {
    setSelectedSite('');
    setSelectedGuard('');
    setSelectedStatus('');
    setSelectedDate(null);
    setVendorF(null);
    setStartDateFilter(null);
    setEndDateFilter(null);
    setFilteredUsers(allTimesheets);
  };

  return (
    <Box sx={{ height: "inherit" }}>
      <Loader loader={loader} />
      <PageTitle title="Timesheets" />

      {checkAuthority("VIEW_TIMESHEETS") && (
        <>
          <Box display="flex" justifyContent="flex-end" sx={{ marginRight: "10px" }}>
            <Button
              variant="contained"
              color="primary"
              sx={{ height: 50 }}
              onClick={clearFilter}
            >
              <FilterAltOffIcon />
              &nbsp;Clear Filter
            </Button>
          </Box>

          <Grid container spacing={2} sx={{ mt: 3, px: 2 }}>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Vendor</InputLabel>
                <Choice
                  label="Vendor"
                  value={vendorF}
                  onChange={(e) => setVendorF(e.target.value)}
                >
                  {vendors.map((vendor) => (
                    <MenuItem key={vendor.id} value={vendor.value}>
                      {vendor.label}
                    </MenuItem>
                  ))}
                </Choice>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Site</InputLabel>
                <Choice
                  label="Site"
                  value={selectedSite}
                  onChange={(e) => setSelectedSite(e.target.value)}
                >
                  {sites.map((site) => (
                    <MenuItem key={site._id} value={site._id}>
                      {site.name}
                    </MenuItem>
                  ))}
                </Choice>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Choice
                  label="Status"
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                >
                  {statusList.map((status) => (
                    <MenuItem key={status.id} value={status.id}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Choice>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth>
                <InputLabel>Guard</InputLabel>
                <Choice
                  label="Guard"
                  value={selectedGuard}
                  onChange={(e) => setSelectedGuard(e.target.value)}
                >
                  {guardList.map((guard) => (
                    <MenuItem key={guard._id} value={guard._id}>
                      {guard.firstname} {guard.lastname}
                    </MenuItem>
                  ))}
                </Choice>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <LocalDateSelector
                label="Start Date"
                value={startDateFilter}
                onChange={(date) => setStartDateFilter(date)}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <LocalDateSelector
                label="End Date"
                value={endDateFilter}
                onChange={(date) => setEndDateFilter(date)}
              />
            </Grid>


          </Grid>

          <Box sx={{ mt: 4, px: 2 }}>
            <TableContainer component={Paper}>
              <Table aria-label="timesheet table" className="responsive-table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableHeader}>Guard Name</TableCell>
                    <TableCell sx={tableHeader}>Week Starting</TableCell>
                    <TableCell sx={tableHeader}>Week Ending</TableCell>
                    <TableCell sx={tableHeader}>Site Name</TableCell>
                    <TableCell sx={tableHeader}>Hours Worked</TableCell>
                    <TableCell sx={tableHeader}>Date Submitted</TableCell>
                    <TableCell sx={tableHeader}>Status</TableCell>
                    <TableCell sx={tableHeader} align="center">Options</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredUsers.length > 0 ? (
                    filteredUsers
                      .slice(page * perPage, page * perPage + perPage)
                      .map((item, index) => (
                        <TableRow key={index}>
                          <TableCell sx={tableData}>
                            {item?.userId?.firstname
                              ? `${item.userId.firstname} ${item.userId.lastname}`
                              : "NA"}
                          </TableCell>
                          <TableCell sx={tableData}>
                            {moment(item.startTime).format("MMM Do YY")}
                          </TableCell>
                          <TableCell sx={tableData}>
                            {moment(item.endTime).format("MMM Do YY")}
                          </TableCell>
                          <TableCell sx={tableData}>
                            {item.siteId?.name || "NA"}
                          </TableCell>
                          <TableCell sx={tableData}>
                            {item.totalHours || 0}
                          </TableCell>
                          <TableCell sx={tableData}>
                            {moment(item.createdAt).format("MMM Do YY")}
                          </TableCell>
                          <TableCell sx={tableData}>
                            {item.statusId?.name || "NA"}
                          </TableCell>
                          <TableCell align="center" sx={tableData}>
                            {(showBothButtons || canView) && (
                              <Link
                                underline="always"
                                sx={{ cursor: "pointer", color: "primary.main" }}
                                onClick={() =>
                                  navigateTo(`${window.location.pathname}/result`, {
                                    state: { id: item._id }
                                  })
                                }
                              >
                                View
                              </Link>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                  ) : (
                    <EmptyTable colSpan={8} />
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={perPages}
                      count={filteredUsers.length}
                      rowsPerPage={perPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      SelectProps={{ native: true }}
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </Box>
        </>
      )}
    </Box>
  );
}
